<script lang="ts">
	import { afterUpdate, beforeUpdate, onDestroy, onMount } from 'svelte';
	import { push } from 'svelte-spa-router';
	import { load_image_from, real_id } from '../../../helpers/interface_helpers';
	import { capFirstChar, trimLastChar } from '../../../helpers/utilities';
	import { format } from 'svelte-i18n';
	import { defaultDashboardSettings } from '../../../helpers/constants';

	import { user_data, user_settings } from 'src/stores/account/user';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { temp_schemes, alarms } from 'src/stores/lists/scenes-derived';
	import { edit } from 'src/stores/ui/edit_lists';
	import { dashboards } from 'src/stores/features/dashboards';
	import { defaultStateType } from 'src/stores/device-runtime/states';
	import DeviceStateSlot from 'src/components/Lists/Devices/DeviceStateSlot.svelte';

	import {
		onStartDrag,
		onEndDrag,
		// requestEditDashboard,
		requestReorderDashboardItems,
		requestDeleteDashboardItem,
		generateThermostatIcon,
		generateAlarmIcon,
		createThermostatDescription,
		createAlarmDescription,
		type AlarmDelayModalType,
		type AlarmDelayTimerType,
	} from '../list-helpers';

	// project components import
	import DeviceWidget from '../Widgets/DeviceWidget.svelte';
	import DeviceMainSlot from '../Devices/DeviceMainSlot.svelte';

	import ControlGroupOnOff from '../../Controls/Group/ControlGroupOnOff.svelte';
	import ControlGroupThermostat from '../../Controls/Group/ControlGroupThermostat.svelte';
	import ControlGroupDirection from '../../Controls/Group/ControlGroupDirection.svelte';

	import ControlsScene from '../Scenes/ControlsScene.svelte';

	import RoomStatus from '../Rooms/RoomLabelList.svelte';
	import AddEditButtons from '../../AddEditButtons.svelte';
	import EmptyList from '../EmptyList.svelte';

	// design system componets imports
	import {
		CardGrid,
		WidgetCard,
		Toolbar,
		Breadcrumbs,
		Label,
		ActionsContainer,
		IconButton,
		Modal,
		Text,
		Button,
		LabelList,
		Container,
		Icon,
		weatherWidgetMinHeight,
	} from '@Shelly/shelly-design-system';
	import DashboardWizard from '../../Dashboards/DashboardWizard/DashboardWizard.svelte';
	import DashboardCustomizeCardModal from './DashboardCustomizeCardModal.svelte';
	import { NON_PREMIUM_WIDGETS_LIMIT, PREMIUM_WIDGETS_LIMIT } from 'src/constants';
	import ControlsThermostat from '../Thermostats/ControlsThermostat.svelte';
	import ControlAlarm from '../Alarms/ControlAlarm.svelte';
	import RoomLabelMain from '../Rooms/RoomLabelMain.svelte';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { getCustomizeLabelList } from 'src/components/DevicesSettings/CustomizeDeviceWizard/customize-device-wizard-helpers';
	import WeatherWidgetCard from './WeatherWidgetCard.svelte';
	import CameraWidget from 'src/components/Camera/Dashboard/CameraWidget.svelte';
	import {
		device_notifications,
		dismissed_notifications,
	} from 'src/stores/features/device-notifications';
	import ControlMedia from 'src/components/Controls/Single/ControlMedia.svelte';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	import EmptyPlaceholder from 'src/components/EmptyPlaceholder.svelte';
	import { debounce } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { getCriticalNotifications } from 'src/helpers/notifications/device_notifications_helper';
	import TimerAlarmDelay from '../Alarms/components/TimerAlarmDelay.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import { mapOldCustomIconFromDBtoNew } from 'src/helpers/new-layout-helpers';

	export let list = [];
	export let id;

	const WIDGETS_LIMIT =
		$user_data.premium || $user_settings.been_premium
			? PREMIUM_WIDGETS_LIMIT
			: NON_PREMIUM_WIDGETS_LIMIT;
	const flipDurationMs = 0;

	const defaultEditItem = {
		roomId: -1,
		position: -1,
		name: '',
		type: '',
		id: -1,
		dashboardId: id || -1,
		selectedType: {
			id: '',
			i18n_sub_name: '',
		},
	};

	let dragDisabled = true;
	let dashboardId = -1;
	let showAddEditModal = false;
	let showDeleteModal = false;
	let showCustomizeCardModal = false;
	let updatedItem: { type: string; id: any } = {
		type: '',
		id: -1,
	};

	let editItem = { ...defaultEditItem };

	let dashListRefs = [];
	let widgetCardRefs: Record<string, HTMLElement> = {};
	let weatherCardMinHeight: number = weatherWidgetMinHeight;
	let alarmsDelayModal: AlarmDelayModalType = {};
	let alarmsDelayTimer: AlarmDelayTimerType = {};

	function afterAddWidget() {
		if (dashListRefs?.[items.length - 1]) {
			dashListRefs[items.length - 1].scrollIntoView({ behavior: 'smooth' });
		}
	}

	const resetWeatherWidgetMinHeight = () => {
		weatherCardMinHeight = weatherWidgetMinHeight;
	};

	const getWeatherWidgetMinHeight = (widgetRefs: Record<string, HTMLElement>) => {
		if ($edit) return;

		const refslist = Object.values(widgetRefs);
		if (refslist?.length === 0) return;

		const weatherCardItem = itemsWithNotifications.filter((item) => item._type === 'weather');

		if (weatherCardItem?.length === 0) return;

		const filterdElementsWithoutWeather = Object.entries(widgetRefs).map(([key, value]) => {
			const refItem = {
				id: key,
				value: value,
			};

			if (refItem.id === weatherCardItem[0].id) return 0;
			const currentHeight = refItem.value?.getBoundingClientRect().height;
			if (currentHeight) {
				return currentHeight;
			}
			return 0;
		});

		const currentMaxHeight = Math.round(Math.max(...filterdElementsWithoutWeather));

		if (currentMaxHeight > weatherWidgetMinHeight) {
			weatherCardMinHeight = currentMaxHeight;
		}
	};

	const onWindowResize = debounce(() => {
		resetWeatherWidgetMinHeight();
		getWeatherWidgetMinHeight(widgetCardRefs);
	}, 50);

	$: (id, resetWeatherWidgetMinHeight());
	$: (dashboardSettings, resetWeatherWidgetMinHeight());
	$: ($edit, resetWeatherWidgetMinHeight());
	$: Object.values(widgetCardRefs) ? getWeatherWidgetMinHeight(widgetCardRefs) : null;

	$: isEmptyDeviceList = Object.values($devices).length === 0;

	$: items = $edit && items ? items : [...list];
	$: itemsWithNotifications = $edit ? items : [...list];
	$: dashboardItemIds = list.map((item) => item?.id).filter(Boolean);
	$: memberStatusStore = createMemberStatusSnapshot(dashboardItemIds);
	$: memberSettingsStore = createMemberSettingsSnapshot(dashboardItemIds);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;

	$: dndSettings = {
		items: itemsWithNotifications,
		flipDurationMs: flipDurationMs,
		dragDisabled: dragDisabled,
		type: 'dashListHolder',
	};

	$: dashboardId = editItem.dashboardId;
	$: dashboardSettings = $dashboards[dashboardId]?.settings || defaultDashboardSettings;

	$: {
		// Doing this so we can get the latest data for notifications :)
		const updatedItems = items?.map((item) => {
			const devicesFilter = Object.keys($devices);

			let notificationsToShow = Object.fromEntries(
				Object.entries($device_notifications).filter(
					([id]) =>
						item.id === id.split('-')[0] &&
						devicesFilter.includes(id.split('-')[0]) &&
						!Object.keys($dismissed_notifications).includes(id),
				),
			);

			const criticalNotifications = getCriticalNotifications(notificationsToShow);

			return {
				...item,
				criticalNotifications,
				...(Object.values(notificationsToShow)[0]?.error && {
					protectNotificationType: Object.values(notificationsToShow)[0]?.error,
				}),
			};
		});

		const hasChanged = JSON.stringify(updatedItems) !== JSON.stringify(itemsWithNotifications);

		if (hasChanged) {
			itemsWithNotifications = updatedItems;
		}
	}

	onMount(() => {
		dashboardId = id;
	});

	onDestroy(() => {
		edit.set(false);
	});

	beforeUpdate(() => {
		if (!$edit) {
			handleEndDrag();
		}
	});

	afterUpdate(() => {
		if (dashboardId !== id) {
			dashboardId = id;
			edit.set(false);
			updatedItem = {
				type: '',
				id: -1,
			};

			setSidePanelState({
				type: 'dash',
				open: false,
			});
		}
	});

	function generateSubtitle(item) {
		// count alarm sensors
		const isAlarm = item._type === 'alarms';
		const sensors = isAlarm
			? item?.if?.or
				? item?.if?.or
				: item?.if?.and // filter out active time conditions if there is an 'and' block
					? item?.if?.and.filter((i) => !i.or.some((i) => i.indailyint)) // active time conditions are the 'indailyint' property
					: []
			: [];
		let value = '';

		if (sensors.length > 0) {
			// show sensors count for alarms
			value = $format('widget_card_subtitle_linked_sensor', {
				values: { totalSensors: sensors.length },
				default: '{totalSensors} sensors linked',
			});
		} else {
			// show room name for other widgets
			value =
				typeof item.room_id !== 'undefined'
					? $rooms[item.room_id]?.name
					: item._meta !== undefined
						? $rooms[item._meta.room]?.name
						: '';
		}
		return value;
	}

	/**
	 * Updates dashboard widget settings based on the item type.
	 *
	 * This function acts as a middle layer to adjust the dashboard widget settings
	 * received from the backend before applying them to the UI. Some widget types
	 * do not support certain parameters, so we modify the settings dynamically to
	 * ensure proper customization.
	 *
	 * @param {Object} item - The dashboard widget item.
	 * @param {string} item._type - The type of the widget (e.g., "rooms", "scenes", "alarms", "groups").
	 * @returns {Object} - The updated dashboard settings with applied customizations.
	 */
	const updateSettingsBasedOnItemType = (item) => {
		const currentDashboardSettings = $dashboards[id]?.settings || defaultDashboardSettings;
		const { haveBigParams, haveSmallParams } = currentDashboardSettings;
		const isRoom = item._type === 'rooms';
		const isSceneOrAlarmOrGroup = ['scenes', 'alarms', 'groups'].includes(item._type);

		let updatedSettings = { ...currentDashboardSettings };

		if (haveBigParams && isRoom) {
			updatedSettings.haveSmallParams = true;
		}

		if (haveSmallParams && isSceneOrAlarmOrGroup) {
			updatedSettings.haveBigParams = true;
		}

		return updatedSettings;
	};

	function generateWidgetCardDescription(item, state) {
		if (!$edit) {
			if (state?.type !== defaultStateType) {
				if (item._type === 'devices') {
					return {
						value: state?.description?.value,
						type: state?.description.type,
					};
				}
			} else {
				return undefined;
			}
		}
	}

	const onWidgetClick = (item) => {
		if ($edit) return;

		let type = item._type === 'weather' ? item._type : trimLastChar(item._type);
		type = type === 'temp_scheme' ? 'thermostat' : type;

		const id = item.id;

		setSidePanelState({ type, id });

		if (type == 'room') {
			push(`/home/${type}/${id}/devices`);
		} else {
			setSidePanelState({ open: true });
		}

		// TODO: Handle all casees. Don't delete the below comment might need the logic
		// switch (type) {
		// 	case 'device':
		// 		break;

		// 	case 'room':
		// 	case 'group':
		// 	case 'scene':
		// 		push(`/home/${type}/${id}`);
		// 		break;

		// 	default:
		// 		break;
		// }
	};

	const generateLeftBadge = (item) => {
		let leftBadge;

		leftBadge = {
			size: 's',
			noRadiusType: 'second',
			type: 'default',
		};

		switch (item._type) {
			case 'devices':
				if (item?.category == 'virtual_device') {
					leftBadge.value = $format('label_virtual_device', {
						default: 'Virtual Device',
					});
					leftBadge.icon = 'far fa-cube';
					leftBadge.iconColor = '--color-success';
				} else if (isPeripheralDevice(item?.type)) {
					leftBadge.value = $format('label_peripheral_device', {
						default: 'Peripheral Device',
					});
					leftBadge.icon = 'far fa-puzzle-piece';
					leftBadge.iconColor = '--color-warning';
				} else if (item?.mode === 'media_player') {
					leftBadge.value = $format('card_status_bar_media_player', {
						default: 'Media',
					});
					leftBadge.icon = 'fas fa-music';
					leftBadge.iconColor = '--color-white';
					// leftBadge.text = $format('card_status_bar_media_player', {
					// 	default: 'Media',
					// });
				} else {
					leftBadge.value = $format('label_left_badge_device', {
						default: 'Device',
					});
					leftBadge.icon = 'fas fa-microchip';
				}
				break;
			case 'groups':
				leftBadge.value = $format('label_left_badge_group', {
					default: 'Group',
				});
				leftBadge.icon = 'fas fa-network-wired';

				break;
			case 'scenes':
				leftBadge.value = $format('label_left_badge_scene', {
					default: 'Scene',
				});
				leftBadge.icon = 'fas fa-film';

				break;
			case 'rooms':
				leftBadge.value = $format('label_left_badge_room', { default: 'Room' });
				leftBadge.icon = 'fas fa-door-open';

				break;
			case 'alarms':
				leftBadge.value = capFirstChar(
					$format('label_left_badge_alarm', {
						default: 'Alarm',
					}),
				);
				leftBadge.icon = 'fas fa-siren-on';

				break;
			case 'temp_schemes':
				leftBadge.value = capFirstChar(
					$format('label_left_badge_thermostat', {
						default: 'Thermostat',
					}),
				);

				leftBadge.icon = 'fas fa-temperature-three-quarters';
				break;
			default:
				break;
		}

		return leftBadge;
	};

	// DND Functions
	const handleStartDrag = () => {
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		let editedList = {};
		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;
			editedList[!editedList[v.id] ? v.id : v.id + v._type || v.type] = {
				...v,
				position: i,
			};
		});

		if (Object.keys(editedList).length === 0) return;

		items = [...e.detail.items];
		handleEndDrag();

		await requestReorderDashboardItems(dashboardId, editedList);
	};

	// Add Edit Modal
	const onEditClick = (id: number, type: string, position?: number, roomId?: number) => {
		editItem = {
			...editItem,
			roomId,
			position,
			name: '',
			type: type,
			id: id,
		};
		showAddEditModal = true;
	};

	// Delete Modal
	const onCloseDeleteModal = () => {
		editItem = {
			...editItem,
			roomId: -1,
			position: -1,
			name: '',
			type: '',
			id: -1,
		};
		showDeleteModal = false;
	};

	const onDeleteClick = (id: number, type: string, name: string) => {
		editItem = {
			...editItem,
			id,
			type,
			name,
			position: -1,
			roomId: -1,
		};
		showDeleteModal = true;
	};

	const onDeleteCard = async () => {
		await requestDeleteDashboardItem(dashboardId, editItem.type, editItem.id);

		// When we are on edit mode items are not reactive
		items = items.filter(
			(item) =>
				item.id !== editItem.id ||
				(item.id === editItem.id && (item._type || item.type) !== editItem.type),
		);

		if (items.length <= 0) edit.set(false);

		onCloseDeleteModal();
	};

	const hideSlotMainStatusParam = (item) => {
		const status = memberStatus[real_id(item.id)];
		const settings = memberSettings[real_id(item.id)];
		const statuses = status_configurator(status, item, settings);
		const labelList = getCustomizeLabelList(item, statuses);
		return labelList.length === 0;
	};
</script>

<svelte:window on:resize={onWindowResize} />

<Toolbar class="mb-m" isSticky>
	<Breadcrumbs
		class="flex-1 min-width-0"
		listClasses="width-full"
		aria-label={$format('label_dashboards', { default: 'Dashboards' })}
		slot="breadcrumbs"
		links={[{ label: $dashboards[dashboardId]?.name }]}
	/>
	<svelte:fragment slot="rightSide">
		{#if !$edit}
			<IconButton
				size="m"
				variant="secondary"
				icon="fas fa-sliders"
				on:click={() => {
					console.log('SHOW MODAL CUSTOM');
					showCustomizeCardModal = true;
				}}
			>
				{$format('button_customize_widget', { default: 'Customize' })}
			</IconButton>
		{/if}

		<AddEditButtons
			disableEditButton={list.length <= 0}
			disableAddButton={isEmptyDeviceList &&
				Object.values($rooms).length <= 0 &&
				!$user_data.premium}
			on:add_click={async () => {
				showAddEditModal = true;
			}}
			hideAddButton={$showNewLayout}
		/>
	</svelte:fragment>
</Toolbar>

<!-- {#if isEmptyDeviceList && list.length <= 0 && Object.values($rooms).length <= 0 && !$user_data.premium}
	<EmptyDeviceList /> -->
{#if list.length <= 0}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column align-items-center">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format('empty_dashboard_label', {
				default: 'Dashboard is empty',
			})}
			description={$format('empty_dashboard_descr', {
				default:
					'Try adding a new widgets for fast access to your favorite devices, scenes, rooms and what ever you need.',
			})}
			btnText={$format('button_add_widget', { default: 'Add widget' })}
			onClick={() => {
				showAddEditModal = true;
			}}
		/>
	</div>
{:else}
	<CardGrid
		sm={158}
		editMode={$edit}
		{dndSettings}
		on:consider={handleReorder}
		on:finalize={handleReorderEnd}
	>
		<!-- Use 'item' as the key instead of 'item.id' to avoid potential duplicates, as 'item' may represent various entities like groups, rooms, and devices.-->
		{#each itemsWithNotifications as item, index (item)}
			<!-- Removing animate:flip={{ duration: flipDurationMs }} because of laggy movement of DND -->
			<div
				class:dnd-placeholder={item?.isDndShadowItem}
				bind:this={dashListRefs[index]}
				tabindex="0"
				role="button"
				aria-label={$format('button_drag_widget_to_reorder', {
					default: 'Drag widget to reorder',
				})}
			>
				{#if item._type === 'weather'}
					<WeatherWidgetCard
						weather={item}
						editMode={$edit}
						bind:widgetCardRef={widgetCardRefs[item.id]}
						minHeight={weatherCardMinHeight}
						on:childWidgetClick={() => onWidgetClick(item)}
					>
						{#if $edit}
							<ActionsContainer>
								<IconButton
									on:mousedown={(e) => onStartDrag(e, $edit, handleStartDrag)}
									on:touchstart={(e) => onStartDrag(e, $edit, handleStartDrag)}
									on:mouseup={(e) => onEndDrag(e, $edit, handleEndDrag)}
									on:touchend={(e) => onEndDrag(e, $edit, handleEndDrag)}
									icon="fas fa-up-down-left-right"
									slot="first"
									dndHandle
									aria-label={$format('aria_drag_widget', {
										default: 'Drag widget',
									})}
								/>
								<IconButton
									icon="fas fa-pen"
									slot="second"
									on:click={(event) => {
										event.stopPropagation();

										onEditClick(item.id, item._type, item.position, item.room_id);
									}}
									aria-label={$format('aria_edit_widget', {
										default: 'Edit widget',
									})}
								/>
								<IconButton
									icon="fas fa-trash"
									variant="danger"
									slot="third"
									on:click={(event) => {
										event.stopPropagation();

										onDeleteClick(
											item.id,
											item._type,
											item?.name || item?._meta?.name,
										);
									}}
									aria-label={$format('aria_delete_widget', {
										default: 'Delete widget',
									})}
								/>
							</ActionsContainer>
						{/if}
					</WeatherWidgetCard>
				{:else if item._type === 'devices' && item?.category === 'camera'}
					<DeviceStateSlot deviceId={item.id} let:deviceState>
						<CameraWidget
							device={item}
							description={generateWidgetCardDescription(item, deviceState)}
							bind:widgetCardRef={widgetCardRefs[item.id]}
							minHeight={weatherCardMinHeight}
							on:childWidgetClick={() => onWidgetClick(item)}
						>
							<svelte:fragment slot="bottom">
								{#if $edit}
									<ActionsContainer>
										<IconButton
											on:mousedown={(e) => onStartDrag(e, $edit, handleStartDrag)}
											on:touchstart={(e) => onStartDrag(e, $edit, handleStartDrag)}
											on:mouseup={(e) => onEndDrag(e, $edit, handleEndDrag)}
											on:touchend={(e) => onEndDrag(e, $edit, handleEndDrag)}
											icon="fas fa-up-down-left-right"
											slot="first"
											dndHandle
											aria-label={$format('aria_drag_widget', {
												default: 'Drag widget',
											})}
										/>
										<IconButton
											icon="fas fa-pen"
											slot="second"
											on:click={(event) => {
												event.stopPropagation();

												onEditClick(
													item.id,
													item._type,
													item.position,
													item.room_id,
												);
											}}
											aria-label={$format('aria_edit_widget', {
												default: 'Edit widget',
											})}
										/>
										<IconButton
											icon="fas fa-trash"
											variant="danger"
											slot="third"
											on:click={(event) => {
												event.stopPropagation();

												onDeleteClick(
													item.id,
													item._type,
													item?.name || item?._meta?.name,
												);
											}}
											aria-label={$format('aria_delete_widget', {
												default: 'Delete widget',
											})}
										/>
									</ActionsContainer>
								{/if}
							</svelte:fragment>
						</CameraWidget>
					</DeviceStateSlot>
				{:else}
					<DeviceStateSlot
						deviceId={item._type === 'devices' ? item.id : undefined}
						let:deviceState
					>
						<WidgetCard
							aria-label={$format('aria_drag_widget', { default: 'Drag widget' })}
							bind:widgetCardRef={widgetCardRefs[item.id]}
							isMedia={item.mode === 'media_player'}
							isFocusable={false}
							title={item.mode !== 'media_player'
								? item?.name || (item?._meta ? item?._meta?.name : '')
								: memberStatus[real_id(item.id)]?.media?.playback?.media_meta?.title}
							subtitle={{ value: generateSubtitle(item) }}
							avatar={{
								image:
									item.mode !== 'media_player'
										? item._type !== 'temp_schemes' &&
											(item.image ||
												(item._meta !== undefined ? item._meta.image : '')) &&
											load_image_from(
												item.image ||
													(item._meta !== undefined ? item._meta.image : ''),
												item?.localImage ? '' : item?.server,
											)
										: memberStatus[real_id(item.id)]?.media?.playback?.media_meta
												?.thumb,

								backgroundColor: item.backgroundColor || item._meta?.backgroundColor,
								icon:
									item.mode === 'media_player'
										? 'fa-light fa-music'
										: item?.icon
											? mapOldCustomIconFromDBtoNew(item.icon)
											: item._type === 'temp_schemes' &&
												generateThermostatIcon(item),
								disabled: item._type === 'devices' ? deviceState?.type == 0 : false,
								notification: item?.criticalNotifications.length
									? { type: 'danger' }
									: null,
							}}
							showFullImage={item._type != 'devices' &&
								item._type != 'alarms' &&
								item._type != 'temp_schemes'}
							editMode={$edit}
							selected={item.id === $sidePanelStateCombined?.id}
							description={generateWidgetCardDescription(item, deviceState)}
							hideSlotLeftSecond
							hideSlotMain={(item._type === 'devices'
								? deviceState?.hideSlotMain
								: false) ||
								(item._type !== 'devices' &&
									item._type !== 'rooms' &&
									item._type !== 'temp_schemes') ||
								!updateSettingsBasedOnItemType(item)?.haveSmallParams ||
								(item._type === 'devices' && hideSlotMainStatusParam(item))}
							hideAvatar={!updateSettingsBasedOnItemType(item)?.havePicture}
							hideTitle={!updateSettingsBasedOnItemType(item)?.haveHeading}
							hideSubtitle={!updateSettingsBasedOnItemType(item)?.haveHeading}
							hideSlotBottom={(!updateSettingsBasedOnItemType(item)?.haveBigParams &&
								!$edit) ||
								(item._type && item?.mainActionType === 'none' && !$edit)}
							hideSlotRight={!item?.criticalNotifications.length}
							on:click={() => onWidgetClick(item)}
						>
							<Label slot="leftBadge" {...generateLeftBadge(item)} />

							<Label
								slot="rightBadge"
								noRadiusType="first"
								value={item?.criticalNotifications[0].name}
								icon="fa-solid fa-shield-exclamation"
								type={item?.protectNotificationType &&
								item?.protectNotificationType === 'mount_error'
									? 'danger'
									: 'warning'}
								size="xs"
							/>

							<svelte:fragment slot="main">
								{#if item._type === 'devices'}
									<DeviceMainSlot device={item} />
								{:else if item._type === 'temp_schemes'}
									<LabelList
										labelList={[
											{
												icon: generateThermostatIcon(item),
												value: createThermostatDescription(
													$temp_schemes[item.id] || item,
													memberStatus[real_id(item.id)]?.thermostat,
												),
											},
										]}
										slot="main"
										type="default"
										size={'m'}
									/>
								{:else if item._type === 'alarms'}
									<LabelList
										labelList={[
											{
												icon: generateAlarmIcon(item),
												value: createAlarmDescription(item),
											},
										]}
										slot="main"
										type="default"
										size={'m'}
									/>
								{:else if item._type == 'rooms'}
									<RoomStatus
										{item}
										size="m"
										on:label-click={() => onWidgetClick(item)}
									/>
								{/if}
							</svelte:fragment>

							<svelte:fragment slot="bottom">
								{#if $edit}
									<ActionsContainer>
										<IconButton
											aria-label={$format('aria_drag_widget', {
												default: 'Drag widget',
											})}
											on:mousedown={(e) => onStartDrag(e, $edit, handleStartDrag)}
											on:touchstart={(e) => onStartDrag(e, $edit, handleStartDrag)}
											on:mouseup={(e) => onEndDrag(e, $edit, handleEndDrag)}
											on:touchend={(e) => onEndDrag(e, $edit, handleEndDrag)}
											icon="fas fa-up-down-left-right"
											slot="first"
											dndHandle
										/>
										<IconButton
											aria-label={$format('aria_edit_widget', {
												default: 'Edit widget',
											})}
											icon="fas fa-pen"
											slot="second"
											on:click={(event) => {
												event.stopPropagation();

												onEditClick(
													item.id,
													item._type,
													item.position,
													item.room_id,
												);
											}}
										/>
										<IconButton
											aria-label={$format('aria_delete_widget', {
												default: 'Delete widget',
											})}
											icon="fas fa-trash"
											variant="danger"
											slot="third"
											on:click={(event) => {
												event.stopPropagation();

												onDeleteClick(
													item.id,
													item._type,
													item?.name || item?._meta?.name,
												);
											}}
										/>
									</ActionsContainer>
								{:else if item._type == 'devices'}
									{#if item.mode === 'media_player'}
										<ControlMedia device={item} />
									{:else}
										<DeviceWidget device={item} />
									{/if}
								{:else if item._type == 'rooms'}
									<RoomLabelMain {item} on:label-click={() => onWidgetClick(item)} />
								{:else if item._type == 'groups'}
									<!-- abstract this to a component or see if we cant reuse the one from Groups list-->
									{#if item.type == 'thermostat'}
										<ControlGroupThermostat group={item} variant="widgetCard" />
									{:else if item.type == 'roller'}
										<ControlGroupDirection group={item} variant="widgetCard" />
									{:else}
										<!-- relays & lights -->
										<ControlGroupOnOff group={item} variant="widgetCard" />
									{/if}
								{:else if item._type == 'scenes'}
									<ControlsScene {item} variant="widgetCard" />
								{:else if item._type == 'alarms'}
									<TimerAlarmDelay
										alarm={$alarms[item.id]}
										hideLabel
										bind:showModal={alarmsDelayModal[item.id]}
										bind:timer={alarmsDelayTimer[item.id]}
									/>

									<ControlAlarm
										item={$alarms[item.id]}
										isOnWidget
										on:alarmDelayStart={(e) => {
											alarmsDelayModal[item.id] = !!e.detail;
										}}
									/>
								{:else if item._type == 'temp_schemes'}
									<ControlsThermostat {item} />
								{/if}

								<!-- TODO Added for scenes -->
							</svelte:fragment>
						</WidgetCard>
					</DeviceStateSlot>
				{/if}
			</div>
		{/each}
	</CardGrid>

	<Modal
		on:close={onCloseDeleteModal}
		showModal={showDeleteModal}
		title={$format('prompt_title_delete_widget', { default: 'Delete widget?' })}
	>
		<Container slot="content" padding="0">
			<div class="d-flex flex-direction-column align-items-center">
				<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
				<Text class="text-bold text-center mb-s" styleType="controls-large">
					{editItem.name}
				</Text>
				<Text class="width-full">
					{$format('on_delete_widget_msg', {
						values: {
							device_name: editItem.name,
						},
						default: `Deleting widget ‘{device_name}’ will remove it from your dashboard.`,
					})}
				</Text>
			</div>
		</Container>

		<div slot="footer" class="d-flex justify-content-end">
			<Button
				variant="secondary"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={onCloseDeleteModal}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={onDeleteCard}
			>
				{$format('button_delete_widget', { default: 'Delete widget' })}
			</Button>
		</div>
	</Modal>
{/if}

{#if showAddEditModal}
	<DashboardWizard
		{dashboardId}
		bind:showAddEditModal
		bind:editItem
		bind:items
		bind:updatedItem
		on:afterAddWidget={afterAddWidget}
	/>
{/if}

{#if showCustomizeCardModal}
	<DashboardCustomizeCardModal bind:showCustomizeCardModal {dashboardId} {dashboardSettings} />
{/if}
